import type { Narrative } from '@medplum/fhirtypes'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import type { JsonLike } from '#src/json.ts'
import { sanitizeFhirString } from '#src/sanitizeFhirString.ts'

/**
 * Walks an outbound FHIR write payload (Resource, Bundle, JSON-Patch op array,
 * etc.) and strips FHIR-disallowed C0 control bytes from every string leaf.
 *
 * Mutates `body` in place; returns void. Mirrors the shape of `addTag`
 * (`#src/tags.ts`), which is the precedent for in-place outbound body
 * mutation in the SeenMedplumClient mixin.
 *
 * Skip rules:
 * - `Narrative.div` — FHIR allows C0 bytes in xhtml; we do not modify it.
 * - `Binary.data` — base64-encoded; sanitizing would corrupt the payload.
 * - For transaction-Bundle PATCH entries (`Binary` with
 *   `application/json-patch+json` content-type), the base64-encoded ops are
 *   decoded, walked recursively against `op.value`, and re-encoded only if
 *   the ops were actually mutated.
 *
 * Empty `extension[]` and `modifierExtension[]` entries — entries whose
 * `valueString` was sanitized to an empty string — are dropped, because
 * Medplum's server-side validator still rejects empty `valueString`
 * (`+` quantifier in the FHIR regex).
 */
export function sanitizeOutboundFhirBody(body: JsonLike | undefined): void {
  if (!isWalkable(body)) {
    return
  }
  walk(body, undefined)
}

type JsonValue = string | number | boolean | null | undefined | WalkNode

interface JsonObject {
  [key: string]: JsonValue
}

type WalkNode = JsonValue[] | JsonObject

function isWalkable(value: JsonLike | undefined): value is WalkNode {
  return value !== null && typeof value === 'object'
}

function walk(node: WalkNode, parentKey: string | undefined): void {
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const value = node[i]
      if (value === null || value === undefined) {
        continue
      }
      if (typeof value === 'string') {
        const sanitized = sanitizeFhirString(value)
        if (sanitized !== value) {
          node[i] = sanitized
        }
        continue
      }
      if (isWalkable(value)) {
        walk(value, undefined)
      }
    }
    if (parentKey === 'extension' || parentKey === 'modifierExtension') {
      pruneEmptyExtensionEntries(node)
    }
    return
  }

  /* Skip Narrative.div — FHIR's only C0-permitting string field. Narrative is
   * always bound to a property named `text` on a DomainResource, so the parent
   * key check disambiguates from any same-shaped non-Narrative object. */
  const isNarrative =
    parentKey === 'text' &&
    'status' in node &&
    'div' in node &&
    typeof (node as { div: unknown }).div === 'string' &&
    isNarrativeStatus((node as { status: unknown }).status)

  /* Skip Binary.data — base64-encoded; sanitizing would corrupt it. We
   * still want to recurse into other Binary fields (`contentType`, `id`,
   * etc.) which are normal strings. */
  const isBinary =
    'resourceType' in node &&
    (node as { resourceType: unknown }).resourceType === 'Binary'

  /* Bundle entries that wrap a JSON-Patch payload: the Binary.data is the
   * base64-encoded JSON-Patch ops. We must decode, walk each op.value, and
   * re-encode if anything changed. */
  if (isBundleEntry(node)) {
    sanitizeEmbeddedPatchEntry(node)
  }

  for (const key of Object.keys(node)) {
    const value = (node as JsonObject)[key]
    if (value === null || value === undefined) {
      continue
    }
    if (typeof value === 'string') {
      if (isNarrative && key === 'div') {
        continue
      }
      if (isBinary && key === 'data') {
        continue
      }
      const sanitized = sanitizeFhirString(value)
      if (sanitized !== value) {
        ;(node as JsonObject)[key] = sanitized
      }
      continue
    }
    if (isWalkable(value)) {
      walk(value, key)
    }
  }
}

function pruneEmptyExtensionEntries(arr: unknown[]): void {
  let writeIdx = 0
  for (let readIdx = 0; readIdx < arr.length; readIdx++) {
    const entry = arr[readIdx]
    if (
      entry !== null &&
      typeof entry === 'object' &&
      'valueString' in entry &&
      (entry as { valueString: unknown }).valueString === ''
    ) {
      continue
    }
    if (writeIdx !== readIdx) {
      arr[writeIdx] = entry
    }
    writeIdx++
  }
  arr.length = writeIdx
}

type NarrativeStatus = NonNullable<Narrative['status']>

function isNarrativeStatus(value: unknown): value is NarrativeStatus {
  return (
    value === 'generated' ||
    value === 'extensions' ||
    value === 'additional' ||
    value === 'empty'
  )
}

interface MaybeBundleEntry {
  request?: { method?: unknown }
  resource?: {
    resourceType?: unknown
    contentType?: unknown
    data?: unknown
  }
}

function isBundleEntry(
  node: JsonObject
): node is JsonObject & MaybeBundleEntry {
  const maybe = node as MaybeBundleEntry
  return (
    typeof maybe.request === 'object' &&
    maybe.request !== null &&
    typeof maybe.resource === 'object' &&
    maybe.resource !== null
  )
}

interface PatchOp {
  op?: string
  path?: string
  value?: unknown
}

function sanitizeEmbeddedPatchEntry(entry: MaybeBundleEntry): void {
  if (entry.request?.method !== 'PATCH') {
    return
  }
  const resource = entry.resource
  if (
    !resource ||
    resource.resourceType !== 'Binary' ||
    typeof resource.data !== 'string' ||
    typeof resource.contentType !== 'string' ||
    !resource.contentType.startsWith('application/json-patch+json')
  ) {
    return
  }

  let ops: PatchOp[]
  try {
    ops = JSON.parse(base64Decode(resource.data))
  } catch {
    return
  }
  if (!Array.isArray(ops)) {
    return
  }

  const before = JSON.stringify(ops)
  for (const op of ops) {
    if (op.value === null || op.value === undefined) {
      continue
    }
    if (typeof op.value === 'string') {
      const sanitized = sanitizeFhirString(op.value)
      if (sanitized !== op.value) {
        op.value = sanitized
      }
      continue
    }
    if (isWalkable(op.value)) {
      walk(op.value, undefined)
    }
  }
  const after = JSON.stringify(ops)
  if (after !== before) {
    resource.data = base64Encode(after)
  }
}
